import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Button, Icon, Spinner } from "@thetreep/ui";
import { useLocation, useNavigate } from "react-router";
import { Solution } from "@gen/travel/v1/search_pb";
import i18n from "@libs/i18n";
import { routes } from "@routes";
import { CabinClass } from "../search/Search";
import { BannerMessage } from "@components/common/BannerMessage";
import {
  LocalDateTimeSchema,
  Offer,
  TrainCabinClass,
  Transportation,
} from "@gen/travel/v1/common_pb";
import {
  isFilteredOffer,
  isFilteredSolution,
  sortSolutions,
} from "../results/FiltersResults/utils";
import { FiltersResults } from "../results/FiltersResults/FiltersResults";
import { SolutionItem } from "../results/Solution";
import OfferItem from "../results/Offer/OfferItem";
import { useExchangeContext } from "./context";
import {
  ExchangeOffer,
  ExchangeServiceSearchResponse,
  ExchangeSolution,
} from "@gen/travel/v1/exchange_pb";
import { OfflineModal } from "@pages/user/bookings/OfflineModal";
import { searchExchange } from "@services/exchange";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { localDateTimeToDate } from "@utils/date";
import { create } from "@bufbuild/protobuf";
import { AgencyInfo } from "@gen/agency/v1/agency_pb";
import {
  PlaneCabinClass as DAPPlaneCabinClass,
  TrainCabinClass as DAPTrainCabinClass,
} from "@gen/dap/v1/dap_pb";

type FiltersState = {
  directJourney: boolean;
  flexibility: string[];
  sortBy: string;
  priceRange: number[];
};

export function ExchangeResults() {
  // deps
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToaster();

  // params
  const { dap, travel, journey } = useExchangeContext();
  const [results, setResults] = useState<ExchangeServiceSearchResponse>();
  const [isOfflineModalOpen, setOfflineModalOpen] = useState(false);

  // state
  const [filtersMobileOpened, setFiltersMobileOpened] =
    useState<boolean>(false);
  const [filters, setFilters] = useState<FiltersState>({
    directJourney: false,
    flexibility: [],
    sortBy: "price",
    priceRange: [0, 0],
  });
  const [selectedOffer, setSelectedOffer] = useState<{
    offer: Offer | ExchangeOffer;
    solution: Solution | ExchangeSolution;
  } | null>(null);
  const selectedTransport = Transportation.TRAIN;
  const [listVisible, setListVisible] = useState<boolean>(true);
  const [selectedOfferClass, setSelectedOfferClass] = useState<CabinClass>(
    TrainCabinClass.SECOND,
  );
  const [agencyInfo, setAgencyInfo] = useState<AgencyInfo>();
  const [isNextLoading, setIsNextLoading] = useState<boolean>(false);
  const [isPreviousLoading, setIsPreviousLoading] = useState<boolean>(false);

  useEffect(() => {
    const searchResponse = location.state?.searchResponse;
    if (!searchResponse) {
      navigate(routes.SEARCH);
    }

    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setResults(searchResponse);
  }, [location]);

  const onSolutionSelect = (
    solution: Solution | ExchangeSolution,
    cabinClass: CabinClass,
  ) => {
    const offer = solution.offers
      .filter((o) => o.cabinClass.value && o.cabinClass.value === cabinClass)
      .at(0);

    if (!offer) {
      return;
    }
    setListVisible(false);
    setSelectedOffer({
      solution,
      offer,
    });
    setSelectedOfferClass(offer.cabinClass.value || TrainCabinClass.SECOND);
  };

  const onClassSelect = (cabinClass: CabinClass) => {
    setSelectedOfferClass(cabinClass);
  };

  const handleConfirmChoice = async (offer: Offer | ExchangeOffer) => {
    if (
      !offer.isCompliant ||
      ("isExchangeable" in offer && offer.isExchangeable === false)
    ) {
      setAgencyInfo(results?.agencyInfo);
      setOfflineModalOpen(true);
      return;
    }

    navigate(
      routes.EXCHANGE_RECAP.replace(":travelId", travel.id).replace(
        ":journeyId",
        journey.id,
      ),
      {
        state: { offer: offer, solution: selectedOffer?.solution },
      },
    );
  };

  const solutions = useMemo(() => {
    if (!results) {
      return [];
    }
    const solutions = results.solutions
      // keep only selected transport
      .filter((solution) =>
        solution.journey?.segments.every(
          (s) => s.transport?.type == selectedTransport,
        ),
      )
      // keep only solutions with offers that match the filters
      .filter((solution) => isFilteredSolution(solution, filters));
    return sortSolutions(solutions, filters);
  }, [results, filters, selectedTransport]);

  const hasSolutions = solutions && solutions.length > 0;

  function printDate(index: number) {
    if (index === 0) {
      return (
        <span className="font-jakarta text-gray-900">
          {departureDate(solutions[0])}
        </span>
      );
    }

    const previousSolutionDeparture =
      solutions[index - 1].journey?.schedule?.departure;
    const currentSolutionDeparture =
      solutions[index].journey?.schedule?.departure;

    if (!previousSolutionDeparture || !currentSolutionDeparture) return;

    const previousDepartureDate = localDateTimeToDate(
      previousSolutionDeparture,
    );
    const currentDepartureDate = localDateTimeToDate(currentSolutionDeparture);

    if (!sameDay(previousDepartureDate, currentDepartureDate)) {
      return (
        <span className="font-jakarta text-gray-900">
          {departureDate(solutions[index])}
        </span>
      );
    }
  }

  function sameDay(a: Date, b: Date) {
    return (
      a.getFullYear() == b.getFullYear() &&
      a.getMonth() == b.getMonth() &&
      a.getDate() == b.getDate()
    );
  }

  function departureDate(solution: Solution | ExchangeSolution): string {
    const departure = solution.journey?.schedule?.departure;
    if (!departure) {
      return "";
    }
    return Intl.DateTimeFormat(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(localDateTimeToDate(departure));
  }

  if (!results || !dap) {
    return;
  }

  const MAX_RETRIES = 2;

  function shiftDate(date: Date, hours: number): Date {
    return new Date(date.getTime() + hours * 60 * 60 * 1000);
  }

  async function fetchWithRetries({
    baseDate,
    type,
    travelId,
    journeyId,
  }: {
    baseDate: string | Date;
    type: "departure" | "arrival";
    travelId: string;
    journeyId: string;
  }) {
    let attempt = 0;
    let currentDate = new Date(baseDate);

    while (attempt <= MAX_RETRIES) {
      const response = await searchExchange(travelId, journeyId, {
        date: currentDate,
        type,
      });

      if (response.solutions?.length > 0) return response;

      const shift = type === "departure" ? 2 : -2;
      currentDate = shiftDate(currentDate, shift);
      attempt++;
    }

    return { solutions: [] };
  }

  async function handlePrevious() {
    if (!results) return;

    try {
      setIsPreviousLoading(true);
      const sorted = [...solutions].sort((a, b) => {
        const aDate = localDateTimeToDate(
          a.journey?.schedule?.arrival || create(LocalDateTimeSchema),
        ).getTime();
        const bDate = localDateTimeToDate(
          b.journey?.schedule?.arrival || create(LocalDateTimeSchema),
        ).getTime();
        return aDate - bDate;
      });

      const baseDate = sorted[0].journey?.schedule?.arrival;
      if (!baseDate) return;

      const response = await fetchWithRetries({
        baseDate: localDateTimeToDate(baseDate),
        type: "arrival",
        travelId: travel?.id,
        journeyId: journey?.id,
      });

      if (response.solutions.length > 0) {
        setResults({
          ...results,
          solutions: [...response.solutions, ...results.solutions],
        });
      } else {
        addToast("info", t("noMoreSolutions"));
      }
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsPreviousLoading(false);
    }
  }

  async function handleNext() {
    if (!results) return;

    try {
      setIsNextLoading(true);
      const sorted = [...solutions].sort((a, b) => {
        const aDate = localDateTimeToDate(
          a.journey?.schedule?.departure || create(LocalDateTimeSchema),
        ).getTime();
        const bDate = localDateTimeToDate(
          b.journey?.schedule?.departure || create(LocalDateTimeSchema),
        ).getTime();
        return aDate - bDate;
      });

      const baseDate = sorted[sorted.length - 1].journey?.schedule?.departure;
      if (!baseDate) return;

      const response = await fetchWithRetries({
        baseDate: localDateTimeToDate(baseDate),
        type: "departure",
        travelId: travel?.id,
        journeyId: journey?.id,
      });

      if (response.solutions.length > 0) {
        setResults({
          ...results,
          solutions: [...results.solutions, ...response.solutions],
        });
      } else {
        addToast("info", t("noMoreSolutions"));
      }
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsNextLoading(false);
    }
  }

  const dapIncludesTrainPremier =
    !dap.trainCabinClass ||
    [DAPTrainCabinClass.PREMIER, DAPTrainCabinClass.UNSPECIFIED].includes(
      dap.trainCabinClass,
    );
  const dapIncludesPlaneBusiness =
    !dap.planeCabinClass ||
    [DAPPlaneCabinClass.BUSINESS, DAPPlaneCabinClass.UNSPECIFIED].includes(
      dap.planeCabinClass,
    );
  const dapIncludesPlaneEconomyPremium =
    !dap.planeCabinClass ||
    [
      DAPPlaneCabinClass.ECONOMY_PREMIUM,
      DAPPlaneCabinClass.UNSPECIFIED,
    ].includes(dap.planeCabinClass);

  return (
    <div className="flex flex-1 flex-col  gap-6 ">
      <h1
        data-testid="journey-header"
        className="text-xl md:text-[32px] font-jakarta font-bold md:mb-6 break-words"
      >
        {`${t("booking.exchange.exchange")}: ${journey.route?.origin?.name} - ${journey.route?.destination?.name}`}
      </h1>

      <div className="flex flex-col gap-6">
        <div className="hidden lg:block">
          <FiltersResults
            isMobileOpened={filtersMobileOpened}
            onFilterChange={setFilters}
            onClose={() => setFiltersMobileOpened(false)}
          />
        </div>
        {!listVisible ? (
          <div className="block lg:hidden">
            <Button
              size="mini"
              width="100%"
              kind="outline"
              startEnhancer={<Icon src="arrow_left_line" />}
              onClick={() => setListVisible(true)}
            >
              {t("results.returnList")}
            </Button>
          </div>
        ) : (
          <div className="block lg:hidden">
            <Button
              size="mini"
              width="100%"
              kind="outline"
              startEnhancer={<Icon color="#0C419A" src="settings_6_line" />}
              onClick={() => setFiltersMobileOpened(true)}
            >
              {t("results.sortAndFilter")}
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <div>
          <div className="flex flex-1 gap-6">
            <div
              className={`flex-1 ${listVisible ? "block" : "hidden"} lg:block`}
            >
              <div className="flex justify-between">
                <div></div>
                <div
                  className={`space-x-4 pr-4 hidden whitespace-nowrap h-0 ${
                    selectedTransport == Transportation.TRAIN
                      ? "xl:flex"
                      : "2xl:flex"
                  } space-x-4`}
                >
                  {selectedTransport == Transportation.TRAIN ? (
                    <>
                      <div className="w-[96px] font-jakarta text-center">
                        {t("results.secondClass")}
                      </div>

                      {dapIncludesTrainPremier && (
                        <div className="w-[96px]  text-center">
                          {t("results.firstClass")}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="w-[96px] font-jakarta text-center">
                        {t("results.economy")}
                      </div>

                      {dapIncludesPlaneEconomyPremium && (
                        <div className="w-[96px] font-jakarta text-center">
                          {t("results.economyPremium")}
                        </div>
                      )}

                      {dapIncludesPlaneBusiness && (
                        <div className="w-[96px] font-jakarta text-center">
                          {t("results.business")}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-4 min-h-[300px]">
                {results.warnings?.map((w, i) => (
                  <BannerMessage>
                    <div key={i} dangerouslySetInnerHTML={{ __html: w }} />
                  </BannerMessage>
                ))}
                {!hasSolutions ? (
                  <BannerMessage>
                    {t("results.noSolutions", {
                      transport:
                        selectedTransport == Transportation.TRAIN
                          ? t("train").toLowerCase()
                          : t("plane").toLowerCase(),
                      openHours: results.agencyInfo?.openingHours,
                    })}
                  </BannerMessage>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={isPreviousLoading}
                      className="flex items-center gap-2 text-cnam-blue text-sm md:text-base font-bold font-jakarta mt-4 mb-6
                        "
                      onClick={() => handlePrevious()}
                    >
                      {isPreviousLoading ? (
                        <Spinner $size={12} />
                      ) : (
                        <Icon src="arrow_up_line" />
                      )}
                      {selectedTransport == Transportation.TRAIN
                        ? t("results.previousTrains")
                        : t("results.previousFlights")}
                    </button>
                    {solutions.map(
                      (
                        solution: Solution | ExchangeSolution,
                        index: number,
                      ) => (
                        <>
                          {printDate(index)}
                          <SolutionItem
                            key={index}
                            dap={dap}
                            solution={solution}
                            index={index}
                            selectedTransport={selectedTransport}
                            selectedClass={
                              selectedOffer?.solution.journey?.id ==
                              solution.journey?.id
                                ? selectedOfferClass
                                : undefined
                            }
                            isFilteredSolution={(solution, classType) =>
                              isFilteredSolution(solution, filters, classType)
                            }
                            isFilteredOffer={(offer) =>
                              isFilteredOffer(offer, filters)
                            }
                            onSolutionSelect={onSolutionSelect}
                          />
                        </>
                      ),
                    )}
                    <button
                      type="button"
                      disabled={isNextLoading}
                      className="flex items-center gap-2 text-cnam-blue text-sm md:text-base font-bold font-jakarta mt-4 mb-6
                        "
                      onClick={() => handleNext()}
                    >
                      {isNextLoading ? (
                        <Spinner $size={12} />
                      ) : (
                        <Icon src="arrow_down_line" />
                      )}
                      {selectedTransport == Transportation.TRAIN
                        ? t("results.nextTrains")
                        : t("results.nextFlights")}
                    </button>
                  </>
                )}
              </div>
            </div>
            {selectedOffer && selectedOffer.solution.journey && (
              <OfferItem
                dap={dap}
                inward={false}
                transport={selectedTransport}
                isFilteredOffer={(offer) => isFilteredOffer(offer, filters)}
                isFilteredSolution={(solution, classType) =>
                  isFilteredSolution(solution, filters, classType)
                }
                solution={selectedOffer.solution}
                onConfirm={handleConfirmChoice}
                cabinClass={selectedOfferClass as CabinClass}
                onClassSelect={onClassSelect}
                listVisible={listVisible}
              />
            )}
          </div>

          <OfflineModal
            isOpen={isOfflineModalOpen}
            onClose={() => setOfflineModalOpen(false)}
            agencyInfo={agencyInfo}
            messageLabel="userBookings.offlineText"
          />
        </div>
      </div>
    </div>
  );
}
